import { useCallback, useEffect, useRef, useState } from "react";
import { Grid2 } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MyWorlist from "./MyWorklist";
import WorkItemDetails from "./WorkItemDetails";
import LaparoLaparoscopyPrescriptionForm from "../Prescriptions/LaproscopyPrescriptionForm";
import PatientDetails from "../Patients/PatientDetails";
import PhysiotherapyTreatmentNotes from "./PhysioTherapyTreatmentNotes";
import NoteTypeDialog from "./NoteTypeDialogue";
import MedicalRecord from "./MedicalRecord";
import GeneralNote from "./GeneralNote";
import { LocalStorageKeys, PrescriptionTypes } from "../Utility/Constants";
import AppointmentDetails from "../Appointments/AppointmentDetails";
import NewBill from "../Billing/NewBill";
import EditPatient from "../Patients/EditPatient";
import { useDebouncedEffect } from "../Utility/Utility";
import AddServicesToVisit from "../Appointments/AddServicesToVisit";
import { clearFormDataFromLocal, getFormDataFromLocal } from "../Utility/FormDataTempStorage";
import UnsavedDataConfirmationDialog from "../common/UnsavedDataConfirmationDialog";
import BaseMedicalRecordForm from "../Prescriptions/BaseMedicalRecordForm";
import Drawing from "./Drawing";
import WorklistDashboard from "./WorklistDashboard";
import AllTasks from "./AllTasks"; // ✅ NEW
import { useContext } from "react";
import AuthContext from "../Context/AuthProvider";
import { UserRolesConstants } from "../Utility/Constants";

function MyWorklistHome() {
  const workItemDetailsRef = useRef();

  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  const [newMedicalRecordTabNumber, setNewMedicalRecordTabNumber] = useState(0);
  const [newNoteTabNumber, setNewNoteTabNumber] = useState(0);

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [tabToClose, setTabToClose] = useState(null);
  const { auth } = useContext(AuthContext);
const isAdmin    = auth?.roles?.includes(UserRolesConstants.FacilityAdmin);
const isPhysician = auth?.roles?.includes(UserRolesConstants.Physician);
const isBoth     = isAdmin && isPhysician;

 const [tabs, setTabs] = useState(() => {
  const savedTabs = localStorage.getItem(LocalStorageKeys.MyWorkListTabs);

  const permanentTabs = [
    { label: "Dashboard", uniqueId: "Dashboard", type: "Dashboard" },
    // My Tasks — sirf physician ya both ko
    ...(isPhysician ? [{ label: "My Tasks", uniqueId: "Worklist", type: "Worklist" }] : []),
    // All Tasks — sirf admin ya both ko
    ...(isAdmin ? [{ label: "All Tasks", uniqueId: "AllTasks", type: "AllTasks" }] : []),
  ];

  if (!savedTabs) return permanentTabs;

  const parsed = JSON.parse(savedTabs);
  const dynamicTabs = parsed.filter(
    (t) => t.uniqueId !== "Dashboard" && t.uniqueId !== "Worklist" && t.uniqueId !== "AllTasks"
  );

  return [...permanentTabs, ...dynamicTabs];
});

  const [tabHistory, setTabHistory] = useState(() => {
    const saved = localStorage.getItem(LocalStorageKeys.MyWorkListTabHistory);
    return saved ? JSON.parse(saved) : ["Worklist"];
  });

  const [activeTab, setActiveTab] = useState(() => {
    const savedActiveTab = localStorage.getItem(LocalStorageKeys.MyWorkListActiveTab);
    return savedActiveTab || "Worklist";
  });

  const onNewRecordSaved = (uniqueId, newRecordId, newRecordDisplayId) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.uniqueId === uniqueId
          ? { ...tab, prescriptionId: newRecordId, label: newRecordDisplayId, uniqueId: newRecordId }
          : tab
      )
    );
    setActiveTab(newRecordId);
  };

  useEffect(() => {
    setTabHistory((prev) => {
      if (prev[prev.length - 1] === activeTab) return prev;
      const filtered = prev.filter((t) => t !== activeTab);
      const newHistory = [...filtered, activeTab];
      return newHistory;
    });
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.MyWorkListTabs, JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.MyWorkListActiveTab, activeTab);
  }, [activeTab]);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditPrescription = (serviceId, prescriptionDetails) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === prescriptionDetails.id);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        return prevTabs;
      } else {
        const newTab = {
          label: prescriptionDetails?.displayId,
          uniqueId: prescriptionDetails?.id,
          type: "EditPrescription",
          serviceId: serviceId,
          prescriptionId: prescriptionDetails?.id,
          prescriptionType: prescriptionDetails.prescriptionType,
        };

        setActiveTab(prescriptionDetails?.id);
        return [...prevTabs, newTab];
      }
    });
  };

  const handleViewVisitDetails = (visitId, visitDisplayId) => {
    const existingTab = tabs.find((tab) => tab.uniqueId === visitId);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    }

    const newTab = {
      label: `Visit - ${visitDisplayId}`,
      uniqueId: visitId,
      type: "visit-details",
      visitId: visitId,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(visitId);
  };

  const openPrescriptionViewTab = (prescriptionId, prescriptionDisplayId, prescriptionType, serviceId) => {
    setTabs((prevTabs) => {
      const existingTab = tabs.find((tab) => tab.uniqueId === prescriptionId);
      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
      } else {
        const newTab = {
          label: prescriptionDisplayId || "Prescription",
          uniqueId: prescriptionId,
          type: "ViewPrescription",
          prescriptionId: prescriptionId,
          prescriptionType: prescriptionType,
          serviceId: serviceId,
        };

        setActiveTab(prescriptionId);
        return [...prevTabs, newTab];
      }
      return prevTabs;
    });
  };

  const handleEditNote = (serviceId, noteDetails) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === noteDetails.id);
      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        return prevTabs;
      } else {
        let tabLabel;
        let tabType;

        if (noteDetails.noteType === "General") {
          tabLabel = noteDetails?.displayId || "General Note";
          tabType = "EditGeneralNote";
        } else if (noteDetails.noteType === "PhysiotherapyTreatment") {
          tabLabel = noteDetails?.displayId || "Physio Note";
          tabType = "EditPhysioNote";
        }

        const newTab = {
          label: tabLabel,
          uniqueId: noteDetails?.id,
          type: tabType,
          serviceId: serviceId,
          noteId: noteDetails?.id,
        };

        setActiveTab(noteDetails?.id);
        return [...prevTabs, newTab];
      }
    });
  };

  const handleEditMedicalRecord = (serviceId, recordDetails) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === recordDetails.id);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        return prevTabs;
      } else {
        const newTab = {
          label: recordDetails?.displayId || "Medical Record",
          uniqueId: recordDetails?.id,
          type: "EditMedicalRecord",
          serviceId: serviceId,
          recordId: recordDetails?.id,
        };

        setActiveTab(recordDetails?.id);
        return [...prevTabs, newTab];
      }
    });
  };

  const handleAddNote = useCallback((serviceId) => {
    setSelectedServiceId(serviceId);
    setNoteDialogOpen(true);
  }, []);

  const handleNoteTypeSelect = (noteType) => {
    setNewNoteTabNumber((prevTabNumber) => {
      const newTabNumber = prevTabNumber + 1;

      let tabLabel;
      let tabType;

      switch (noteType) {
        case "General":
          tabLabel = newTabNumber === 1 ? "General Notes" : `General Notes ${newTabNumber}`;
          tabType = "AddGeneralNote";
          break;
        case "Physiotherapy":
          tabLabel = newTabNumber === 1 ? "Physio Notes" : `Physio Notes ${newTabNumber}`;
          tabType = "AddPhysioNote";
          break;
        case "FrozenShoulder":
          tabLabel = newTabNumber === 1 ? "Frozen Shoulder Notes" : `Frozen Shoulder Notes ${newTabNumber}`;
          tabType = "AddFrozenShoulderNote";
          break;
        default:
          tabLabel = "Notes";
          tabType = "AddGeneralNote";
      }

      const newTab = {
        label: tabLabel,
        uniqueId: `AddNote-${noteType}-${newTabNumber}`,
        type: tabType,
        serviceId: selectedServiceId,
      };

      setTabs((prevTabs) => [...prevTabs, newTab]);
      setActiveTab(`AddNote-${noteType}-${newTabNumber}`);

      return newTabNumber;
    });
    setNoteDialogOpen(false);
  };

  const handleAddMedicalRecord = useCallback((serviceId) => {
    setNewMedicalRecordTabNumber((prevTabNumber) => {
      const newTabNumber = prevTabNumber + 1;
      const newTab = {
        label: newTabNumber === 1 ? "Add Medical Record" : `Add Medical Record ${newTabNumber}`,
        uniqueId: `AddMedicalRecord-${newTabNumber}`,
        type: "AddMedicalRecord",
        serviceId: serviceId,
      };

      setTabs((prevTabs) => [...prevTabs, newTab]);
      setActiveTab(`AddMedicalRecord-${newTabNumber}`);

      return newTabNumber;
    });
  }, []);

  const handleAddTLDraw = useCallback((serviceId) => {
    const uniqueId = `AddTLDraw-${serviceId}-${Date.now()}`;
    const newTab = {
      label: " Drawing",
      uniqueId: uniqueId,
      type: "AddTLDraw",
      serviceId: serviceId,
    };
    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(uniqueId);
  }, []);

  const handleOpenSavedTLDraw = useCallback((diagramId, name, serviceId) => {
    const tabUniqueId = `Drawing-${diagramId}`;
    const existingTab = tabs.find((tab) => tab.uniqueId === tabUniqueId);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    }
    const newTab = {
      label: name || "Drawing",
      uniqueId: tabUniqueId,
      type: "AddTLDraw",
      serviceId: serviceId,
      diagramId: diagramId,
    };
    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(tabUniqueId);
  }, [tabs]);

  const handleViewDetails = (serviceDetails) => {
    const existingTab = tabs.find((tab) => tab.uniqueId === serviceDetails.id);

    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
    } else {
      const newTab = {
        label: serviceDetails?.displayId,
        uniqueId: serviceDetails?.id,
        type: "WorkItemDetails",
        serviceId: serviceDetails?.id,
      };

      setTabs((prevTabs) => [...prevTabs, newTab]);
      setActiveTab(serviceDetails?.id);
    }
  };

  const handleNewPrescription = (serviceId, prescriptionType = "GeneralPrescription", patientName = "") => {
    let tabLabel;
    if (prescriptionType === PrescriptionTypes.PathologyLabReport) {
      tabLabel = "New Lab Report - " + patientName;
    } else if (prescriptionType === PrescriptionTypes.PhysiotherapyPrescription) {
      tabLabel = "New Physio Rx - " + patientName;
    } else if (prescriptionType === PrescriptionTypes.FrozenShoulder) {
      tabLabel = "New Frozen Shoulder Rx - " + patientName;
    } else if (prescriptionType === PrescriptionTypes.GeneralPrescription) {
      tabLabel = "New General Rx - " + patientName;
    } else if (prescriptionType === PrescriptionTypes.DentalPrescription) {
      tabLabel = "New Dental Rx - " + patientName;
    }

    const existingTab = tabs.find((tab) => tab.uniqueId === `${serviceId}-${prescriptionType}`);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    } else {
      const newTab = {
        label: tabLabel,
        type: "NewPrescription",
        serviceId: serviceId,
        uniqueId: `${serviceId}-${prescriptionType}`,
        prescriptionType: prescriptionType,
      };
      setTabs((prevTabs) => [...prevTabs, newTab]);
      setActiveTab(newTab.uniqueId);
    }
  };

  const handleViewPatientDetails = (patientData) => {
    const existingTab = tabs.find((tab) => tab.uniqueId === `PatientDetails-${patientData.id}`);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    }

    const newTab = {
      label: `${patientData.firstName} ${patientData.lastName}`,
      uniqueId: `PatientDetails-${patientData.id}`,
      type: "patient-details",
      patientData: patientData,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.uniqueId);
  };

  const closeCurrentTabAndSwitchTab = () => {
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.uniqueId !== activeTab));
    setTabHistory((prev) => {
      const filtered = prev.filter((t) => t !== activeTab);
      const remainingTabs = tabs
        .filter((tab) => tab.uniqueId !== activeTab)
        .map((tab) => tab.uniqueId);
      const lastActive = [...filtered].reverse().find((t) => remainingTabs.includes(t));
      setActiveTab(lastActive || "Worklist");
      localStorage.setItem(LocalStorageKeys.MyWorkListTabHistory, JSON.stringify(filtered));
      return filtered;
    });
  };

  const handleCloseTab = (tabValue) => {
    // ✅ All three permanent tabs are non-closeable
    if (tabValue === "MyWorklist" || tabValue === "Dashboard" || tabValue === "AllTasks") return;
    const unsavedData = getFormDataFromLocal(tabValue);
    if (unsavedData) {
      setTabToClose(tabValue);
      setShowUnsavedChangesDialog(true);
      return;
    }
    closeTab(tabValue);
  };

  const closeTab = (tabValue) => {
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.uniqueId !== tabValue));
    setTabHistory((prev) => {
      const filtered = prev.filter((t) => t !== tabValue);
      if (activeTab === tabValue) {
        const remainingTabs = tabs
          .filter((tab) => tab.uniqueId !== tabValue)
          .map((tab) => tab.uniqueId);
        const lastActive = [...filtered].reverse().find((t) => remainingTabs.includes(t));
        setActiveTab(lastActive || "Worklist");
      }
      localStorage.setItem(LocalStorageKeys.MyWorkListTabHistory, JSON.stringify(filtered));
      return filtered;
    });
  };

  const handleDeletePrescription = (serviceId, prescriptionId) => {
    setTabs((prevTabs) => {
      const updatedTabs = prevTabs.filter((tab) => tab.uniqueId !== prescriptionId);
      const existingTab = updatedTabs.find((tab) => tab.uniqueId === serviceId);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        workItemDetailsRef.current?.refreshPrescriptions();
      } else {
        setActiveTab("Worklist");
      }

      return updatedTabs;
    });
  };

  const handleDeleteNote = (serviceId, noteId) => {
    setTabs((prevTabs) => {
      const updatedTabs = prevTabs.filter((tab) => tab.uniqueId !== noteId);
      const existingTab = updatedTabs.find((tab) => tab.uniqueId === serviceId);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        workItemDetailsRef.current?.refreshClinicalNotes();
      } else {
        setActiveTab("Worklist");
      }

      return updatedTabs;
    });
  };

  const handleDeleteMedicalRecord = (serviceId, recordId) => {
    setTabs((prevTabs) => {
      const updatedTabs = prevTabs.filter((tab) => tab.uniqueId !== recordId);
      const existingTab = updatedTabs.find((tab) => tab.uniqueId === serviceId);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        workItemDetailsRef.current?.refreshMedicalRecords();
      } else {
        setActiveTab("Worklist");
      }

      return updatedTabs;
    });
  };

  const handleSavePrescription = (serviceId, prescriptionId, prescriptionStatus) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === serviceId);
      if (existingTab) {
        workItemDetailsRef.current?.refreshPrescriptions();
      }
      return prevTabs;
    });
  };

  const handleSaveNote = (serviceId, noteId, noteStatus) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === serviceId);
      if (existingTab) {
        workItemDetailsRef.current?.refreshClinicalNotes();
      }
      return prevTabs;
    });
  };

  const openBillTab = (billData) => {
    const existingTab = tabs.find((tab) => tab.uniqueId === `EditBill-${billData.id}`);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    }

    const newTab = {
      label: `Bill - ${billData.displayId}`,
      uniqueId: `EditBill-${billData.id}`,
      type: "edit-bill",
      billData: billData,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.uniqueId);
  };

  const handleAddServices = (visitData) => {
    const existingTab = tabs.find((tab) => tab.uniqueId === `AddServices-${visitData.id}`);
    if (existingTab) {
      setActiveTab(existingTab.uniqueId);
      return;
    }

    const newTab = {
      label: `Add Services - ${visitData.displayId}`,
      uniqueId: `AddServices-${visitData.id}`,
      type: "add-services",
      visitData: visitData,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.uniqueId);
  };

  const handleSaveMedicalRecord = (serviceId, recordId, recordStatus) => {
    setTabs((prevTabs) => {
      if (recordStatus === "Final") {
        clearFormDataFromLocal(recordId);
        const updatedTabs = prevTabs.filter((tab) => tab.uniqueId !== recordId);
        const existingTab = updatedTabs.find((tab) => tab.uniqueId === serviceId);
        if (existingTab) {
          setActiveTab(existingTab.uniqueId);
          workItemDetailsRef.current?.refreshMedicalRecords();
        } else {
          setActiveTab("Worklist");
        }
        return updatedTabs;
      } else {
        const existingTab = prevTabs.find((tab) => tab.uniqueId === serviceId);
        if (existingTab) {
          workItemDetailsRef.current?.refreshMedicalRecords();
        }
        return prevTabs;
      }
    });
  };

  useDebouncedEffect(
    () => { localStorage.setItem(LocalStorageKeys.MyWorkListTabs, JSON.stringify(tabs)); },
    [tabs],
    500,
  );

  useDebouncedEffect(
    () => { localStorage.setItem(LocalStorageKeys.MyWorkListActiveTab, activeTab); },
    [activeTab],
    500,
  );

  useDebouncedEffect(
    () => { localStorage.setItem(LocalStorageKeys.MyWorkListTabHistory, JSON.stringify(tabHistory)); },
    [tabHistory],
    500,
  );

  const handleEditPatientTab = (patient) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find((tab) => tab.uniqueId === `EditPatient-${patient.id}`);

      if (existingTab) {
        setActiveTab(existingTab.uniqueId);
        return prevTabs;
      }

      const newTab = {
        label: `Edit ${patient.firstName} ${patient.lastName}`,
        uniqueId: `EditPatient-${patient.id}`,
        type: "EditPatient",
        patient: patient,
      };

      setActiveTab(newTab.uniqueId);
      return [...prevTabs, newTab];
    });
  };

  const handleDiscardChanges = () => {
    const targetTab = tabToClose || activeTab;
    clearFormDataFromLocal(targetTab);
    closeTab(targetTab);
    setShowUnsavedChangesDialog(false);
    setTabToClose(null);
  };

  const handleSaveEdit = (updatedPatient) => {
    const updatedTabs = tabs.filter((tab) => !tab.uniqueId.startsWith("EditPatient"));
    setTabs(updatedTabs);

    const patientDetailsTab = tabs.find(
      (tab) => tab.uniqueId === `PatientDetails-${updatedPatient.id}`,
    );
    if (patientDetailsTab) {
      setActiveTab(patientDetailsTab.uniqueId);
    } else {
      setActiveTab("Patients");
    }
  };

  return (
    <>
      <Grid2 container>
        <NoteTypeDialog
          open={noteDialogOpen}
          onClose={() => setNoteDialogOpen(false)}
          onSelect={handleNoteTypeSelect}
        />

        <TabContext value={activeTab}>
          <Grid2
            container
            size={12}
            sx={{ borderBottom: "1px solid lightgray", width: "100%" }}
          >
            <Tabs
              value={activeTab}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.uniqueId}
                  sx={{ paddingLeft: "5px" }}
                  label={
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>{tab.label}</span>
                      {/* ✅ All three permanent tabs have no close button */}
                      {tab.uniqueId !== "Worklist" &&
                        tab.uniqueId !== "Dashboard" &&
                        tab.uniqueId !== "AllTasks" && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTab(tab.uniqueId);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                    </span>
                  }
                  value={tab.uniqueId}
                />
              ))}
            </Tabs>
          </Grid2>

          {/* Worklist Tab */}
          <TabPanel
            key="Worklist"
            value="Worklist"
            sx={{ padding: "0px", width: "100%" }}
            // keepMounted
          >
            <MyWorlist
              onViewDetails={handleViewDetails}
              onAddNewPrescription={handleNewPrescription}
              onAddNote={handleAddNote}
              onAddMedicalRecord={handleAddMedicalRecord}
              onViewVisitDetails={handleViewVisitDetails}
              onViewPatientDetails={handleViewPatientDetails}
              onAddTLDraw={handleAddTLDraw}
            />
          </TabPanel>

          {/* Dashboard Tab */}
          <TabPanel
            key="Dashboard"
            value="Dashboard"
            sx={{ padding: "0px", width: "100%" }}
            // keepMounted
          >
            <WorklistDashboard />
          </TabPanel>

          {/* ✅ All Tasks Tab */}
          <TabPanel
            key="AllTasks"
            value="AllTasks"
            sx={{ padding: "0px", width: "100%" }}
            // keepMounted
          >
            <AllTasks
              onViewDetails={handleViewDetails}
              onViewVisitDetails={handleViewVisitDetails}
              onViewPatientDetails={handleViewPatientDetails}
            />
          </TabPanel>

          {/* Dynamic tabs */}
          {tabs
            .filter(
              (tab) =>
                tab.uniqueId !== "Worklist" &&
                tab.uniqueId !== "Dashboard" &&
                tab.uniqueId !== "AllTasks",
            )
            .map((tab) => (
              <TabPanel
                key={tab.uniqueId}
                value={tab.uniqueId}
                sx={{ padding: "0px", width: "100%" }}
                keepMounted
              >
                {/* Work Item Details */}
                {tab.type === "WorkItemDetails" && (
                  <WorkItemDetails
                    ref={workItemDetailsRef}
                    key={tab.serviceId}
                    serviceId={tab.serviceId}
                    onEditPrescription={handleEditPrescription}
                    onAddNewPrescription={handleNewPrescription}
                    onViewPrescription={openPrescriptionViewTab}
                    onAddNote={handleAddNote}
                    onViewNote={handleEditNote}
                    onAddMedicalRecord={handleAddMedicalRecord}
                    onViewMedicalRecord={handleEditMedicalRecord}
                    onViewPatientDetails={handleViewPatientDetails}
                    onViewVisitDetails={handleViewVisitDetails}
                    onAddTLDraw={handleAddTLDraw}
                    onOpenSavedTLDraw={handleOpenSavedTLDraw}
                  />
                )}

                {tab.type === "NewPrescription" &&
                  (tab.prescriptionType === PrescriptionTypes.PhysiotherapyPrescription ||
                    tab.prescriptionType === PrescriptionTypes.FrozenShoulder ||
                    tab.prescriptionType === PrescriptionTypes.GeneralPrescription ||
                    tab.prescriptionType === PrescriptionTypes.PathologyLabReport ||
                    tab.prescriptionType === PrescriptionTypes.DentalPrescription) && (
                    <BaseMedicalRecordForm
                      key={tab.uniqueId}
                      uniqueId={tab.uniqueId}
                      recordId={tab.prescriptionId}
                      onNewRecordSaved={onNewRecordSaved}
                      serviceId={tab.serviceId}
                      onSaveRecord={handleSavePrescription}
                      onRemoveRecord={handleDeletePrescription}
                      formType={tab.prescriptionType}
                    />
                  )}

                {tab.type === "EditPrescription" &&
                  (tab.prescriptionType === PrescriptionTypes.PhysiotherapyPrescription ||
                    tab.prescriptionType === PrescriptionTypes.FrozenShoulder ||
                    tab.prescriptionType === PrescriptionTypes.GeneralPrescription ||
                    tab.prescriptionType === PrescriptionTypes.PathologyLabReport ||
                    tab.prescriptionType === PrescriptionTypes.DentalPrescription) && (
                    <BaseMedicalRecordForm
                      key={tab.uniqueId}
                      uniqueId={tab.uniqueId}
                      recordId={tab.prescriptionId}
                      serviceId={tab.serviceId}
                      onSaveRecord={handleSavePrescription}
                      onRemoveRecord={handleDeletePrescription}
                      formType={tab.prescriptionType}
                    />
                  )}

                {tab.type === "ViewPrescription" && (
                  <BaseMedicalRecordForm
                    key={tab.prescriptionId}
                    uniqueId={tab.prescriptionId}
                    serviceId={tab.serviceId}
                    recordId={tab.prescriptionId}
                    formType={tab.prescriptionType}
                  />
                )}

                {tab.type === "AddGeneralNote" && (
                  <GeneralNote
                    key={tab.serviceId}
                    serviceId={tab.serviceId}
                    uniqueId={tab.serviceId + "-" + tab.type}
                    onSaveNote={handleSaveNote}
                    onRemoveNote={handleDeleteNote}
                  />
                )}

                {tab.type === "AddPhysioNote" && (
                  <PhysiotherapyTreatmentNotes
                    key={tab.serviceId}
                    serviceId={tab.serviceId}
                    uniqueId={tab.serviceId + "-" + tab.type}
                    onSaveNote={handleSaveNote}
                    onRemoveNote={handleDeleteNote}
                  />
                )}

                {tab.type === "EditGeneralNote" && (
                  <GeneralNote
                    key={tab.noteId}
                    serviceId={tab.serviceId}
                    uniqueId={tab.serviceId + "-" + tab.noteId}
                    noteId={tab.noteId}
                    onSaveNote={handleSaveNote}
                    onRemoveNote={handleDeleteNote}
                  />
                )}

                {tab.type === "EditPhysioNote" && (
                  <PhysiotherapyTreatmentNotes
                    key={tab.noteId}
                    uniqueId={tab.serviceId + "-" + tab.noteId}
                    serviceId={tab.serviceId}
                    noteId={tab.noteId}
                    onSaveNote={handleSaveNote}
                    onRemoveNote={handleDeleteNote}
                  />
                )}

                {tab.type === "AddMedicalRecord" && (
                  <MedicalRecord
                    key={`add-medical-record-${tab.serviceId}`}
                    availedServiceId={tab.serviceId}
                    onNewRecordSaved={onNewRecordSaved}
                    onRemoveMedicalRecord={handleDeleteMedicalRecord}
                    uniqueId={tab.uniqueId}
                  />
                )}

                {tab.type === "AddTLDraw" && (
                  <Drawing
                    key={tab.uniqueId}
                    serviceId={tab.serviceId}
                    uniqueId={tab.uniqueId}
                    diagramId={tab.diagramId || null}
                  />
                )}

                {tab.type === "patient-details" && (
                  <PatientDetails
                    key={tab?.uniqueId}
                    patientId={tab?.patientData?.id}
                    onViewDocument={openPrescriptionViewTab}
                    onOpenTab={handleEditPatientTab}
                  />
                )}
                {tab.type === "EditPatient" && (
                  <EditPatient patient={tab.patient} onSave={handleSaveEdit} />
                )}
                {tab.type === "visit-details" && (
                  <AppointmentDetails
                    key={tab?.uniqueId}
                    selectedVisitId={tab?.visitId}
                    onEditBill={openBillTab}
                    onViewPatientDetails={handleViewPatientDetails}
                    onViewWorklistDetails={handleViewDetails}
                    onViewDocument={openPrescriptionViewTab}
                    onAddServices={handleAddServices}
                  />
                )}
                {tab.type === "edit-bill" && (
                  <NewBill key={tab?.uniqueId} billId={tab?.billData?.id} />
                )}
                {tab.type === "add-services" && (
                  <AddServicesToVisit
                    key={tab?.uniqueId}
                    visitId={tab?.visitData?.id}
                    visitDisplayId={tab?.visitData?.displayId}
                    onViewPatientDetails={handleViewPatientDetails}
                    onEditBill={openBillTab}
                  />
                )}
              </TabPanel>
            ))}
        </TabContext>
      </Grid2>

      <UnsavedDataConfirmationDialog
        key={tabToClose || activeTab}
        open={showUnsavedChangesDialog}
        onClose={() => {
          setShowUnsavedChangesDialog(false);
          setTabToClose(null);
        }}
        onDiscardChanges={handleDiscardChanges}
        title="Unsaved Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        discardButtonText="Discard Changes"
        cancelButtonText="Keep Changes"
      />
    </>
  );
}

export default MyWorklistHome;